import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { fetchAuthSession } from 'aws-amplify/auth';
import envConfig from 'env/EnvConfig';

let _cachedCreds = null;
let _cachedCredsExpiry = 0;

export async function getGuestCredentials() {
  return fromCognitoIdentityPool({
    clientConfig: { region: envConfig.AWS_PROJECT_REGION },
    identityPoolId: envConfig.AWS_IDENTITY_POOL_ID,
  })();
}

/**
 * Returns credentials suited for KMS:
 * - Tries Amplify (authenticated) credentials first
 * - Falls back to Cognito Identity Pool unauth credentials
 * - If requireAuth is true, throws when no authenticated creds
 */
export async function getKmsCredentials({ requireAuth = false } = {}) {
  const now = Date.now();
  if (_cachedCreds && _cachedCredsExpiry > now) {
    return _cachedCreds;
  }
  
  try {
    const session = await fetchAuthSession();
    const creds = session?.credentials;
    
    if (creds?.accessKeyId && creds?.secretAccessKey) {
      const expMs = creds.expiration ? new Date(creds.expiration).getTime() : now + 10 * 60 * 1000;
      _cachedCreds = {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        sessionToken: creds.sessionToken,
        expiration: creds.expiration
      };
      _cachedCredsExpiry = Math.max(now + 30 * 1000, expMs - 60 * 1000);
      return _cachedCreds;
    }
    
    if (requireAuth) {
      throw new Error('Authenticated AWS credentials not available.');
    }
  } catch (_) {
    if (requireAuth) {
      throw new Error('Failed to obtain authenticated AWS credentials.');
    }
  }
  
  // fallback to guest creds (pre-login), requires unauth enabled + IAM perms
  const guestCreds = await getGuestCredentials();
  _cachedCreds = {
    accessKeyId: guestCreds.accessKeyId,
    secretAccessKey: guestCreds.secretAccessKey,
    sessionToken: guestCreds.sessionToken,
    expiration: guestCreds.expiration
  };
  _cachedCredsExpiry = now + 10 * 60 * 1000;
  return _cachedCreds;
}